import React, { useCallback, useEffect, useState } from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { useNoticeCount } from 'contexts/NoticeCountContext';

import ToolbarPopover from 'components/layout/ToolbarPopover';
import { NOTICE_TYPE_KEY, NoticeType } from 'stores/NoticeAdminStoreAdapter';
import {
  UserNoticeGroupResult,
  UserNoticeResult,
  deleteUserNotice,
  listUserNotice,
  resolveUserNotice,
} from 'stores/NoticeStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';
import { errorDefault, successDefault } from 'utils/Toastify';

// --- 種別設定 ---

type NoticeTypeConfig = {
  icon: React.ReactNode;
  accentColor: string;
  chipColor: 'primary' | 'error' | 'info' | 'warning';
};

const noticeTypeConfigs: Record<NoticeType, NoticeTypeConfig> = {
  NOTICE: {
    icon: <NotificationsOutlinedIcon />,
    accentColor: 'primary.main',
    chipColor: 'primary',
  },
  IMPORTANT: {
    icon: <NewReleasesOutlinedIcon />,
    accentColor: 'error.main',
    chipColor: 'error',
  },
  INFO: {
    icon: <InfoOutlinedIcon />,
    accentColor: 'info.main',
    chipColor: 'info',
  },
  SECURITY_CHECK: {
    icon: <VerifiedUserOutlinedIcon />,
    accentColor: 'warning.main',
    chipColor: 'warning',
  },
};

// --- Props ---

type UserNoticePopupProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** 表示する通知種別 */
  noticeType: NoticeType;
};

/**
 * ツールバー通知アイコンから開く簡易通知ポップアップ。
 * 指定種別の未解決通知を最新順に一覧表示し、アクション操作も可能。
 */
const UserNoticePopup: React.FC<UserNoticePopupProps> = ({
  anchorEl,
  open,
  onClose,
  noticeType,
}) => {
  const { refresh: refreshBadge } = useNoticeCount();
  const [groups, setGroups] = useState<UserNoticeGroupResult[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchNotices = useCallback(() => {
    setLoading(true);
    listUserNotice({ isResolved: false, noticeType })
      .then(setGroups)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [noticeType]);

  useEffect(() => {
    if (open) fetchNotices();
  }, [open, fetchNotices]);

  const handleDelete = (noticeId: string) => {
    deleteUserNotice({ noticeId })
      .then(() => {
        successDefault('通知を削除しました');
        fetchNotices();
        refreshBadge();
      })
      .catch((e: Error) => {
        errorDefault('通知の削除に失敗しました', e.message);
      });
  };

  const handleResolve = (noticeId: string) => {
    resolveUserNotice({ noticeId })
      .then(() => {
        successDefault('通知を解決済みにしました');
        fetchNotices();
        refreshBadge();
      })
      .catch((e: Error) => {
        errorDefault('通知の解決に失敗しました', e.message);
      });
  };

  const handleLink = (notice: UserNoticeResult) => {
    if (notice.externalLink) {
      window.open(notice.externalLink, '_blank', 'noopener,noreferrer');
    } else if (notice.internalLink) {
      window.open(notice.internalLink, '_blank', 'noopener,noreferrer');
      onClose();
    }
  };

  // 該当種別の通知を日時降順
  const notices: UserNoticeResult[] = (
    groups.find((g) => g.noticeType === noticeType)?.notices || []
  ).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const cfg = noticeTypeConfigs[noticeType];
  const title =
    noticeType === NOTICE_TYPE_KEY.NOTICE
      ? '通知'
      : noticeType === NOTICE_TYPE_KEY.INFO
        ? 'お知らせ'
        : '重要なお知らせ';

  return (
    <ToolbarPopover open={open} anchorEl={anchorEl} onClose={onClose} width={400}>
      {/* ヘッダー */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
      </Box>

      {/* ローディング */}
      {loading ? (
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : notices.length > 0 ? (
        <Stack divider={<Divider />}>
          {notices.slice(0, 20).map((notice) => {
            const hasLink = !!notice.externalLink || !!notice.internalLink;
            return (
              <Box
                key={notice.noticeId}
                sx={{
                  display: 'flex',
                  gap: 1.25,
                  px: 2,
                  py: 1.25,
                  transition: 'background-color 0.15s',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: cfg.accentColor,
                    flexShrink: 0,
                  }}
                >
                  {React.cloneElement(cfg.icon as React.ReactElement<{ sx?: object }>, {
                    sx: { fontSize: 18 },
                  })}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>
                      {notice.title}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
                      {formatDate(notice.createdAt)}
                    </Typography>
                    {notice.isNew && (
                      <Chip
                        label="NEW"
                        size="small"
                        color={cfg.chipColor}
                        sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700 }}
                      />
                    )}
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.4,
                    }}
                  >
                    {notice.content}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.25} sx={{ mt: 0.5 }}>
                    {/* アクションボタン */}
                    {noticeType === NOTICE_TYPE_KEY.NOTICE && (
                      <Tooltip title="削除" placement="top">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(notice.noticeId)}
                          sx={{ '&:hover': { color: 'error.main' } }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(noticeType === NOTICE_TYPE_KEY.INFO ||
                      noticeType === NOTICE_TYPE_KEY.IMPORTANT) && (
                      <Tooltip title="解決済みにする" placement="top">
                        <IconButton
                          size="small"
                          onClick={() => handleResolve(notice.noticeId)}
                          sx={{ '&:hover': { color: 'success.main' } }}
                        >
                          <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasLink && (
                      <Tooltip
                        title={notice.externalLink ? '外部リンクを開く' : '詳細へ移動'}
                        placement="top"
                      >
                        <IconButton size="small" onClick={() => handleLink(notice)}>
                          {notice.externalLink ? (
                            <OpenInNewIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <LinkIcon sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {title}はありません
          </Typography>
        </Box>
      )}
    </ToolbarPopover>
  );
};

export default UserNoticePopup;
